import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import styled from '@emotion/styled';
import { scrollbarCss } from 'app/spacing';
import { hasInputControl, MaybeInputControl } from '../InputControl';
import { useKeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import Validation, { useValidation } from '../Validation';
import { applyRef } from '../props';
import { getMaskedValue } from '../helpers';
import { markdownPreviewStyles } from 'components/WYSIWYG/MarkdownEditor/styles';
import { UnderlineLayout } from './Underline';
import { MarkdownOverlay } from 'components/WYSIWYG/MarkdownEditor/components/MarkdownOverlay';
import { MarkdownEditor } from 'components/WYSIWYG/MarkdownEditor';
import {
  getHTMLValue,
  getRawTextValue,
} from 'components/WYSIWYG/MarkdownEditor/helpers';
import { useTooltip } from '__components/Kizen/Tooltip';

const UnderlineTextarea = styled.div`
  width: 100%;
  overflow-y: auto;
  ${scrollbarCss}
  height: 82px;
  resize: none;
  padding-bottom: 10px;
  & * {
    font-size: 14px;
  }

  ${markdownPreviewStyles}
`;

const StyledLayout = styled(UnderlineLayout)`
  input:focus + & {
    border-color: var(--color-button-secondary-hover);
  }
`;

const StyledTarget = styled.div`
  height: 6px;
`;

const StyledInput = styled.input`
  // hides input from view
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  z-index: -1;
  pointer-events: none;
`;

export const UnderlineMarkdownLongText = forwardRef((props, ref) => {
  const {
    fieldId,
    className,
    value,
    label,
    placeholder,
    disabled,
    error,
    onChange,
    onFocus,
    onBlur,
    fieldType,
    tooltipLabel,
    tooltipMaxWdith,
    displayMaskedValue,
    isMasked = false,
    inModal = true,
    ...others
  } = props;

  const displayValue =
    displayMaskedValue &&
    isMasked &&
    value &&
    `<p>${getMaskedValue(value)}</p>`;

  const { assignFieldHandle } = useKeyBoardContext();

  const validationRef = useRef();
  const mergeRef = useCallback(
    (el) => {
      applyRef(validationRef, el);
      applyRef(ref, el);
    },
    [ref]
  );
  const [validation, validationProps] = useValidation(validationRef, props);

  const targetRef = useRef(null);
  const editorRef = useRef(null);
  const inputRef = useRef(null);

  const {
    'data-field-type': _1,
    'data-field-id': _2,
    ...forInputControl
  } = props;

  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState('');

  const handleOpen = () => {
    if (!disabled || html.trim()) {
      setOpen(true);
    }
  };

  const handleHide = async (away = false) => {
    const editor = editorRef.current?.editor;
    if (editor && !disabled) {
      const value = await getRawTextValue(editor, true);
      if (onChange) {
        onChange(value.trim());
      }
    }
    setOpen(false);
    if (!away) {
      inputRef.current?.focus();
    }
  };

  const onHideAway = () => {
    handleHide(true);
  };

  const onHide = () => {
    handleHide(false);
  };

  const handleSave = (value) => {
    if (!disabled) {
      if (onChange) {
        onChange(value.trim());
      }
    }
    setOpen(false);
    inputRef.current?.focus();
  };

  useEffect(() => {
    const convertAction = async () => {
      const html = await getHTMLValue(value, true, {
        condensed: true,
      });
      setHtml(html);
    };
    convertAction();
  }, [value]);

  const editRef = useRef(false);

  const handleFocus = (...args) => {
    onFocus?.(...args);
    editRef.current = true;
  };

  const handleBlur = (...args) => {
    onBlur?.(...args);
    editRef.current = false;
  };

  const openIfEdit = () => {
    if (editRef.current) {
      setOpen(true);
    }
  };

  assignFieldHandle(fieldId, {
    customTab: () => {
      onHideAway();
    },
    customFocus: () => {
      handleOpen();
      inputRef.current?.focus();
      return validationRef.current;
    },
    customEnter: (e) => {
      if (!open) {
        return false;
      } else {
        const isSubmit = e.ctrlKey || e.metaKey;
        if (isSubmit) {
          onHideAway();
        }
        return !isSubmit;
      }
    },
    customUp: openIfEdit,
    customDown: openIfEdit,
    customSpace: openIfEdit,
    customInput: openIfEdit,
    customEscape: () => {
      if (open) {
        onHide();
      }
    },
    disabled,
  });

  useEffect(() => {
    if (others.forceValidation) {
      validation.onSubmission?.();
    }
  }, [others.forceValidation]); // eslint-disable-line react-hooks/exhaustive-deps

  const [tooltipProps, tooltip] = useTooltip(
    {
      html,
      maxWidth: 500,
    },
    () => !!displayValue && !open && !isMobile
  );

  return (
    <MaybeInputControl variant="underline" forInput {...forInputControl}>
      <StyledTarget ref={targetRef} />
      {tooltip}
      <StyledInput onFocus={handleFocus} onBlur={handleBlur} ref={inputRef} />
      <StyledLayout
        ref={mergeRef}
        className={!hasInputControl(props) && className}
        disabled={disabled}
        readOnly={disabled}
        error={error || validation.error}
        onClick={handleOpen}
      >
        <UnderlineTextarea
          dangerouslySetInnerHTML={{
            __html: disabled
              ? displayValue || html || '—'
              : displayValue ||
                html ||
                (placeholder && `<p>${placeholder}</p>`) ||
                '',
          }}
          color={
            !displayValue && !html && placeholder
              ? 'var(--color-background-medium)'
              : 'var(--color-font-primary)'
          }
          disabled
          {...others}
          {...tooltipProps}
        />
      </StyledLayout>
      <Validation
        inModal={inModal}
        errorPlacement={props.errorPlacement}
        onErrorShow={props.onErrorShow}
        onErrorHide={props.onErrorHide}
        {...validationProps}
      />
      <MarkdownOverlay
        show={open}
        onHide={onHideAway}
        targetRef={targetRef}
        containerRef={mergeRef}
        inModal={inModal}
      >
        <MarkdownEditor
          isMarkdown
          initialValue={value}
          autofocus="end"
          onSave={handleSave}
          editorRef={editorRef}
          readonly={disabled}
          onBlur={(ev) => {
            validation.onBlur?.(ev);
          }}
          inModal={inModal}
        />
      </MarkdownOverlay>
    </MaybeInputControl>
  );
});
